import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@/components/icon/Icon";
import { McpGuide } from "@/pages/admin/McpGuide";
import { PresetTable } from "@/pages/admin/PresetTable";
import { api, ApiError } from "@/lib/api";
import { formatKst } from "@/lib/datetime";
import { diffPricingBody, type PricingChange } from "@/lib/pricing-diff";
import { moveItem, OPTION_GROUP_LABEL, validatePricingBody } from "@/lib/pricing-draft";
import { formatWon, normalizeConfig } from "@/lib/quote";
import { iconButtonClass, inputClass, numberInputClass } from "@/pages/admin/fieldClasses";
import type {
  PricingBody,
  PricingConfigDTO,
  PricingGroup,
  PricingItem,
  PricingProjectType,
  PricingOption,
  PricingRevisionListResponse,
} from "@/types/pricing";

/** 유형·티어는 group 이 없고 옵션만 있어요 — 표 하나로 다루려고 optional 로 뒀어요. */
type EditableItem = PricingItem & { group?: PricingGroup; page_amount?: number; applies_to?: string[] };

function ItemTable({
  title,
  hint,
  items,
  amountHeader,
  withGroup = false,
  withPageAmount = false,
  typeKeys,
  onChange,
}: {
  title: string;
  hint: string;
  items: EditableItem[];
  amountHeader: string;
  withGroup?: boolean;
  /** 옵션 표에서 "붙는 유형" 을 고를 수 있게 넘겨요. 비우면 모든 유형에 붙어요. */
  typeKeys?: { key: string; label: string }[];
  /** 프로젝트 유형만 페이지당 금액을 따로 들고 있어요. */
  withPageAmount?: boolean;
  onChange: (next: EditableItem[]) => void;
}) {
  const update = (index: number, patch: Partial<EditableItem>) =>
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="wds-headline-2 text-label-strong">{title}</h2>
        <p className="wds-caption-1 text-label-assistive">{hint}</p>
      </div>

      <div className="overflow-x-auto rounded-lg bg-background-elevated shadow-normal">
        <table className="w-full min-w-[52rem] wds-body-2">
          <thead>
            <tr className="border-line-alternative border-b text-label-assistive">
              <th className="px-3 py-3 text-left font-medium">키</th>
              <th className="px-3 py-3 text-left font-medium">라벨</th>
              <th className="px-3 py-3 text-left font-medium">설명</th>
              {withGroup && <th className="px-3 py-3 text-left font-medium">그룹</th>}
              <th className="px-3 py-3 text-right font-medium">{amountHeader}</th>
              {withPageAmount && <th className="px-3 py-3 text-right font-medium">페이지당</th>}
              {typeKeys && <th className="px-3 py-3 text-left font-medium">붙는 유형</th>}
              <th className="px-3 py-3 text-right font-medium">순서·삭제</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`${item.key}-${index}`} className="border-line-alternative border-b align-top">
                <td className="px-3 py-2">
                  <input
                    aria-label={`${title} ${index + 1}번 키`}
                    value={item.key}
                    onChange={(e) => update(index, { key: e.target.value })}
                    className={`${inputClass} w-32`}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    aria-label={`${title} ${index + 1}번 라벨`}
                    value={item.label}
                    onChange={(e) => update(index, { label: e.target.value })}
                    className={`${inputClass} w-40`}
                  />
                </td>
                <td className="px-3 py-2">
                  <textarea
                    aria-label={`${title} ${index + 1}번 설명`}
                    value={item.desc}
                    onChange={(e) => update(index, { desc: e.target.value })}
                    rows={2}
                    className={`${inputClass} min-w-[18rem]`}
                  />
                </td>
                {withGroup && (
                  <td className="px-3 py-2">
                    <select
                      aria-label={`${title} ${index + 1}번 그룹`}
                      value={item.group ?? "feature"}
                      onChange={(e) => update(index, { group: e.target.value as PricingGroup })}
                      className={`${inputClass} w-32`}
                    >
                      {(Object.keys(OPTION_GROUP_LABEL) as PricingGroup[]).map((group) => (
                        <option key={group} value={group}>
                          {OPTION_GROUP_LABEL[group]}
                        </option>
                      ))}
                    </select>
                  </td>
                )}
                <td className="px-3 py-2">
                  <input
                    aria-label={`${title} ${index + 1}번 금액`}
                    type="number"
                    min={0}
                    step={10_000}
                    value={item.amount}
                    onChange={(e) => update(index, { amount: Number.parseInt(e.target.value, 10) || 0 })}
                    className={`${numberInputClass} w-32`}
                  />
                </td>
                {typeKeys && (
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-2">
                      {typeKeys.map((type) => {
                        const scoped = item.applies_to ?? [];
                        const checked = scoped.includes(type.key);
                        return (
                          <label key={type.key} className="wds-caption-1 flex items-center gap-1 text-label-neutral">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                update(index, {
                                  applies_to: checked
                                    ? scoped.filter((k) => k !== type.key)
                                    : [...scoped, type.key],
                                })
                              }
                              className="h-4 w-4 accent-primary"
                            />
                            {type.label}
                          </label>
                        );
                      })}
                    </div>
                    <span className="wds-caption-1 text-label-assistive">
                      {(item.applies_to ?? []).length === 0 ? "모든 유형" : null}
                    </span>
                  </td>
                )}
                {withPageAmount && (
                  <td className="px-3 py-2">
                    <input
                      aria-label={`${title} ${index + 1}번 페이지당 금액`}
                      type="number"
                      min={0}
                      step={10_000}
                      value={item.page_amount ?? 0}
                      onChange={(e) => update(index, { page_amount: Number.parseInt(e.target.value, 10) || 0 })}
                      className={`${numberInputClass} w-32`}
                    />
                  </td>
                )}
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      aria-label="위로"
                      disabled={index === 0}
                      onClick={() => onChange(moveItem(items, index, -1))}
                      className={iconButtonClass}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label="아래로"
                      disabled={index === items.length - 1}
                      onClick={() => onChange(moveItem(items, index, 1))}
                      className={iconButtonClass}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      aria-label="삭제"
                      onClick={() => onChange(items.filter((_, i) => i !== index))}
                      className={`${iconButtonClass} text-status-negative`}
                    >
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="px-3 py-3">
          <button
            type="button"
            onClick={() =>
              onChange([
                ...items,
                {
                  key: "",
                  label: "",
                  desc: "",
                  amount: 0,
                  ...(withGroup ? { group: "feature" as PricingGroup } : {}),
                  ...(withPageAmount ? { page_amount: 0 } : {}),
                  ...(typeKeys ? { applies_to: [] } : {}),
                },
              ])
            }
            className="h-9 rounded-md bg-background-alternative px-4 wds-caption-1 font-semibold text-label-neutral"
          >
            항목 추가
          </button>
        </div>
      </div>
    </section>
  );
}

/** 변경 한 줄마다 어느 그룹인지 앞에 붙여 보여줘요. */
function ChangeList({ changes }: { changes: PricingChange[] }) {
  return (
    <ul className="flex flex-col gap-1">
      {changes.map((change) => (
        <li key={`${change.group}:${change.text}`} className="flex gap-2 wds-body-2 text-label-normal">
          <span className="shrink-0 text-label-assistive">{change.group}</span>
          <span>{change.text}</span>
        </li>
      ))}
    </ul>
  );
}

export function PricingPanel({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [config, setConfig] = useState<PricingConfigDTO | null>(null);
  const [draft, setDraft] = useState<PricingBody | null>(null);
  const [revisions, setRevisions] = useState<PricingRevisionListResponse | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  // 저장을 누르면 발행 전 확인 단계로 들어가요.
  const [confirming, setConfirming] = useState(false);

  const handleApiError = useCallback(
    (err: unknown, fallback: string) => {
      if (err instanceof ApiError && err.status === 401) {
        onLogout();
        return;
      }
      setErrors([err instanceof ApiError ? err.message : fallback]);
    },
    [onLogout],
  );

  const loadRevisions = useCallback(async () => {
    try {
      setRevisions(await api.get<PricingRevisionListResponse>("/admin/pricing-config/revisions", token));
    } catch (err) {
      handleApiError(err, "변경 이력을 불러오지 못했어요.");
    }
  }, [token, handleApiError]);

  useEffect(() => {
    api
      .get<PricingConfigDTO>("/pricing-config")
      .then((next) => {
        const normalized = normalizeConfig(next);
        setConfig(normalized);
        setDraft(normalized.body);
      })
      .catch((err) => handleApiError(err, "단가를 불러오지 못했어요."));
    loadRevisions();
  }, [loadRevisions, handleApiError]);

  const changes = useMemo(
    () => (config && draft ? diffPricingBody(config.body, draft) : []),
    [config, draft],
  );

  const updateDraft = (next: PricingBody) => {
    // 편집을 다시 시작하면 확인 단계는 무효예요 (확인한 내용과 발행할 내용이 달라지니까).
    setConfirming(false);
    setNotice("");
    setDraft(next);
  };

  const applyPublished = (published: PricingConfigDTO, message: string) => {
    const normalized = normalizeConfig(published);
    setConfig(normalized);
    setDraft(normalized.body);
    setErrors([]);
    setNotice(message);
    setConfirming(false);
    setShowChanges(false);
    loadRevisions();
  };

  /** 1단계: 검증하고 변경 내용을 펼쳐 확인을 받아요. */
  const handleReview = () => {
    if (!draft) return;
    const found = validatePricingBody(draft);
    setErrors(found);
    setNotice("");
    if (found.length > 0) return;
    setShowChanges(true);
    setConfirming(true);
  };

  /** 2단계: 실제 발행. */
  const handlePublish = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const published = await api.put<PricingConfigDTO>("/admin/pricing-config", draft, token);
      applyPublished(published, `버전 ${published.version} 으로 저장했어요.`);
    } catch (err) {
      handleApiError(err, "저장하지 못했어요.");
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  const handleRollback = async (version: number) => {
    if (!window.confirm(`버전 ${version} 내용으로 되돌릴까요? 새 버전으로 다시 발행돼요. 그 버전에 없던 항목(예시 프리셋 등)은 사라져요.`))
      return;
    try {
      const published = await api.post<PricingConfigDTO>(
        `/admin/pricing-config/revisions/${version}/rollback`,
        {},
        token,
      );
      applyPublished(published, `버전 ${version} 내용을 버전 ${published.version} 으로 발행했어요.`);
    } catch (err) {
      handleApiError(err, "되돌리지 못했어요.");
    }
  };

  if (!draft || !config) {
    return (
      <div className="flex flex-col gap-3">
        {errors.map((e) => (
          <output key={e} className="wds-body-2 text-status-negative">
            {e}
          </output>
        ))}
        {errors.length === 0 && <p className="wds-body-2 text-label-assistive">단가를 불러오는 중이에요…</p>}
      </div>
    );
  }

  const discountPct = Math.round(draft.online_discount_rate * 100);

  return (
    <div className="flex flex-col gap-8">
      <div className="sticky top-0 z-10 flex flex-col gap-3 bg-background-normal py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <p className="wds-body-2 text-label-neutral">
              현재 서비스 중인 버전 <span className="font-semibold tabular-nums">{config.version}</span> ·{" "}
              {formatKst(config.updated_at)}
            </p>
            <span className="wds-label-1 rounded-full bg-background-alternative px-3 py-1 text-label-neutral">
              변경 <span className="font-semibold tabular-nums">{changes.length}</span>건
            </span>
            {changes.length > 0 && (
              <button
                type="button"
                onClick={() => setShowChanges((v) => !v)}
                aria-expanded={showChanges}
                className="wds-label-1 text-primary hover:underline"
              >
                {showChanges ? "간단히 보기 ▴" : "자세하게 보기 ▾"}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={handleReview}
            disabled={saving || changes.length === 0 || confirming}
            className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 wds-body-2 font-semibold text-static-white transition-colors duration-200 ease-standard hover:bg-primary-strong disabled:opacity-50"
          >
            저장하기
          </button>
        </div>

        {showChanges && changes.length > 0 && !confirming && (
          <div className="rounded-md bg-background-alternative px-4 py-3">
            <ChangeList changes={changes} />
          </div>
        )}

        {confirming && (
          <div className="flex flex-col gap-3 rounded-md border border-primary bg-background-elevated px-4 py-4">
            <p className="wds-headline-2 text-label-strong">이 내용으로 발행할까요?</p>
            <ChangeList changes={changes} />
            <p className="wds-caption-1 text-label-assistive">
              발행하면 버전 {config.version + 1} 이 되고, 사이트 견적에 바로 반영돼요. 이력에서 되돌릴 수 있어요.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handlePublish}
                disabled={saving}
                className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-5 wds-body-2 font-semibold text-static-white transition-colors duration-200 ease-standard hover:bg-primary-strong disabled:opacity-50"
              >
                {saving ? "발행 중…" : "발행하기"}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={saving}
                className="inline-flex h-10 items-center justify-center rounded-md bg-background-alternative px-5 wds-body-2 font-semibold text-label-neutral disabled:opacity-50"
              >
                다시 편집
              </button>
            </div>
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <output className="flex flex-col gap-1 rounded-md bg-background-alternative px-4 py-3">
          {errors.map((e) => (
            <span key={e} className="wds-body-2 text-status-negative">
              {e}
            </span>
          ))}
        </output>
      )}
      {notice && (
        <output className="flex items-center gap-2 rounded-md bg-background-alternative px-4 py-3 wds-body-2 text-primary">
          <Icon name="circleCheckFill" size={16} />
          {notice}
        </output>
      )}

      <section className="flex flex-col gap-3">
        <h2 className="wds-headline-2 text-label-strong">전역 값</h2>
        <div className="grid gap-4 rounded-lg bg-background-elevated p-6 shadow-normal sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pricing-max-pages" className="wds-label-1 text-label-strong">
              페이지 수 상한
            </label>
            <input
              id="pricing-max-pages"
              type="number"
              min={1}
              step={1}
              value={draft.max_pages}
              onChange={(e) => updateDraft({ ...draft, max_pages: Number.parseInt(e.target.value, 10) || 1 })}
              className={numberInputClass}
            />
            <span className="wds-caption-1 text-label-assistive">견적 화면에서 이 수보다 크게 입력할 수 없어요.</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pricing-discount" className="wds-label-1 text-label-strong">
              온라인 할인율 (%)
            </label>
            <input
              id="pricing-discount"
              type="number"
              min={0}
              max={90}
              step={1}
              value={discountPct}
              onChange={(e) =>
                updateDraft({ ...draft, online_discount_rate: (Number.parseInt(e.target.value, 10) || 0) / 100 })
              }
              className={numberInputClass}
            />
            <span className="wds-caption-1 text-label-assistive">견적 카드의 "N% 할인" 배지에 그대로 쓰여요.</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pricing-cap" className="wds-label-1 text-label-strong">
              구축비 상한
            </label>
            <input
              id="pricing-cap"
              type="number"
              min={1}
              step={100_000}
              value={draft.onetime_cap}
              onChange={(e) => updateDraft({ ...draft, onetime_cap: Number.parseInt(e.target.value, 10) || 0 })}
              className={numberInputClass}
            />
            <span className="wds-caption-1 text-label-assistive">
              할인가가 이 금액을 넘지 않아요 ({formatWon(draft.onetime_cap)}).
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pricing-free-months" className="wds-label-1 text-label-strong">
              월 유지보수 무료 개월
            </label>
            <input
              id="pricing-free-months"
              type="number"
              min={0}
              max={24}
              step={1}
              value={draft.monthly_free_months}
              onChange={(e) =>
                updateDraft({ ...draft, monthly_free_months: Number.parseInt(e.target.value, 10) || 0 })
              }
              className={numberInputClass}
            />
            <span className="wds-caption-1 text-label-assistive">0이면 프로모션을 표시하지 않아요.</span>
          </div>
        </div>
      </section>

      <ItemTable
        title="프로젝트 유형"
        hint="하나만 선택하는 유형이에요. 금액은 기본 구축비, 페이지당은 화면이 하나 늘 때 더해지는 금액이에요. 첫 항목이 기본 선택돼요."
        items={draft.project_types}
        amountHeader="기본 구축비"
        withPageAmount
        onChange={(next) => updateDraft({ ...draft, project_types: next as PricingProjectType[] })}
      />

      <ItemTable
        title="옵션"
        hint="여러 개 선택하는 항목이에요. 기능은 그리드에, 추가 옵션은 체크박스 줄로 보여요. 붙는 유형을 비우면 모든 유형에서 보여요."
        typeKeys={draft.project_types.map((t) => ({ key: t.key, label: t.label || t.key }))}
        items={draft.options}
        amountHeader="1회성 추가금"
        withGroup
        onChange={(next) =>
          updateDraft({
            ...draft,
            options: next.map((item) => ({ ...item, group: item.group ?? "feature" })) as PricingOption[],
          })
        }
      />

      <ItemTable
        title="월 지원"
        hint="티어라서 하나만 선택되고 합산되지 않아요. 금액은 월 정액이고, 라벨 뒤 '· 월 N원' 문구는 금액에서 자동으로 붙어요."
        items={draft.support_tiers}
        amountHeader="월 정액"
        onChange={(next) => updateDraft({ ...draft, support_tiers: next as PricingItem[] })}
      />

      <PresetTable body={draft} onChange={(next) => updateDraft({ ...draft, presets: next })} />

      <section className="flex flex-col gap-3">
        <h2 className="wds-headline-2 text-label-strong">변경 이력</h2>
        <div className="overflow-x-auto rounded-lg bg-background-elevated shadow-normal">
          <table className="w-full wds-body-2">
            <thead>
              <tr className="border-line-alternative border-b text-label-assistive">
                <th className="px-4 py-3 text-left font-medium">버전</th>
                <th className="px-4 py-3 text-left font-medium">발행 시각</th>
                <th className="px-4 py-3 text-right font-medium">되돌리기</th>
              </tr>
            </thead>
            <tbody>
              {revisions?.items.map((r) => (
                <tr key={r.version} className="border-line-alternative border-b">
                  <td className="px-4 py-3 tabular-nums text-label-normal">
                    {r.version}
                    {r.version === revisions.active_version && (
                      <span className="ml-2 rounded-full bg-primary px-2 py-0.5 wds-caption-1 font-semibold text-static-white">
                        서비스 중
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-label-neutral">{formatKst(r.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleRollback(r.version)}
                      disabled={r.version === revisions.active_version}
                      className="h-8 rounded-md bg-background-alternative px-3 wds-caption-1 font-semibold text-label-neutral disabled:opacity-40"
                    >
                      이 버전으로
                    </button>
                  </td>
                </tr>
              ))}
              {revisions?.items.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-label-assistive">
                    이력이 없어요.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <McpGuide />
    </div>
  );
}

export default PricingPanel;
